import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useId, useState } from "react";
import { ChevronDown, CircleHelp, X } from "lucide-react";
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from "../../../components/ui/dialog";

function DeviceInstructions({ title, expanded, onToggle, children }) {
  const contentId = useId();

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-white">
      <h3>
        <button
          type="button"
          onClick={onToggle}
          className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-semibold text-(--color-text-primary) hover:bg-workspace/40 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
          aria-expanded={expanded}
          aria-controls={contentId}
        >
          <span>{title}</span>
          <ChevronDown className={`h-4 w-4 shrink-0 text-primary transition-transform ${expanded ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
      </h3>
      <div id={contentId} hidden={!expanded} className="border-t border-border bg-workspace/25 px-4 py-3 text-sm leading-6 per-file-hint">
        {children}
      </div>
    </section>
  );
}

export function LocationTroubleshootingModal() {
  const [expandedIndex, setExpandedIndex] = useState(-1);
  const toggleDevice = (index) => setExpandedIndex((current) => current === index ? -1 : index);
  return (
    <Dialog onOpenChange={() => setExpandedIndex(-1)}>
      <DialogPrimitive.Trigger asChild>
        <button
          type="button"
          className="text-xs text-accent underline underline-offset-2 cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          Troubleshoot location issues
        </button>
      </DialogPrimitive.Trigger>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-xs" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[88vh] w-[calc(100%-2rem)] max-w-3xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-border bg-white shadow-2xl focus:outline-hidden"
        >
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-5 py-4">
            <div className="flex min-w-0 items-center gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                <CircleHelp className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <DialogTitle className="font-semibold text-(--color-text-primary) leading-normal tracking-normal">
                  Troubleshoot location issues
                </DialogTitle>
                <DialogPrimitive.Description className="text-sm per-file-hint">
                  Check the location settings for your device, browser, and website.
                </DialogPrimitive.Description>
              </div>
            </div>
            <DialogPrimitive.Close
              type="button"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-lg per-file-hint hover:bg-workspace/60 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              aria-label="Close location troubleshooting"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </DialogPrimitive.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5 text-sm leading-6 text-(--color-text-primary) [&_code]:whitespace-normal [&_code]:break-words [&_code]:rounded [&_code]:bg-workspace/60 [&_code]:px-1 [&_code]:text-xs">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide per-file-hint">Instructions by device</h2>
            <div className="space-y-2">
            <DeviceInstructions title="Android Devices" expanded={expandedIndex === 0} onToggle={() => toggleDevice(0)}>
              <ol className="list-decimal space-y-4 pl-5 marker:font-semibold">
                <li>
                  <h4 className="font-semibold">System Level (Device Location Toggle)</h4>
                  <ul className="mt-1 list-disc space-y-2 pl-5">
                    <li><strong>Location Master Switch:</strong> Must be toggled <strong>ON</strong> under <code>Settings &gt; Location</code>.</li>
                    <li><strong>Google Location Accuracy:</strong> Recommended <strong>ON</strong> under <code>Settings &gt; Location &gt; Location Services &gt; Google Location Accuracy</code> (uses Wi-Fi, cellular, and sensors alongside GPS for faster acquisition).</li>
                  </ul>
                </li>
                <li>
                  <h4 className="font-semibold">App Level (Browser or Native App Permissions)</h4>
                  <ul className="mt-1 list-disc space-y-2 pl-5">
                    <li><strong>Location Permission:</strong> Under <code>Settings &gt; Apps &gt; [Browser Name, e.g., Chrome] &gt; Permissions &gt; Location</code>, set to <strong>&quot;Allow only while using the app&quot;</strong> or <strong>&quot;Ask every time&quot;</strong>.</li>
                    <li><strong>Use Precise Location:</strong> Toggled <strong>ON</strong> under the browser&apos;s location permission setting (if off, the app receives only an approximate location within ~3 km<sup>2</sup>).</li>
                  </ul>
                </li>
                <li>
                  <h4 className="font-semibold">Browser / Site Level (In-App Settings)</h4>
                  <ul className="mt-1 list-disc space-y-2 pl-5">
                    <li><strong>Site Settings:</strong> Inside the browser (e.g., Chrome <code>Settings &gt; Site settings &gt; Location</code>), Ensure Location access is <strong>Allowed/Ask</strong>.</li>
                    <li><strong>Origin Prompt:</strong> The specific website origin must be granted permission when prompted by the browser (<code>Allow</code>).</li>
                  </ul>
                </li>
              </ol>
            </DeviceInstructions>
            <DeviceInstructions title="iOS Devices (iPhone)" expanded={expandedIndex === 1} onToggle={() => toggleDevice(1)}>
              <ol className="list-decimal space-y-4 pl-5 marker:font-semibold">
                <li>
                  <h4 className="font-semibold">System Level (Device Location Toggle)</h4>
                  <ul className="mt-1 list-disc space-y-2 pl-5">
                    <li><strong>Location Services:</strong> Must be toggled <strong>ON</strong> under <code>Settings &gt; Privacy &amp; Security &gt; Location Services</code>.</li>
                  </ul>
                </li>
                <li>
                  <h4 className="font-semibold">App Level (Browser or Native App Permissions)</h4>
                  <ul className="mt-1 list-disc space-y-2 pl-5">
                    <li><strong>Location Access:</strong> Under <code>Settings &gt; Privacy &amp; Security &gt; Location Services &gt; [Browser Name, e.g., Safari or Chrome]</code>, set to <strong>&quot;While Using the App&quot;</strong> or <strong>&quot;Ask Next Time Or When I Share&quot;</strong>.</li>
                    <li><strong>Precise Location:</strong> Toggled <strong>ON</strong> inside that same browser setting menu (disabling it restricts access to a broad region).</li>
                  </ul>
                </li>
                <li>
                  <h4 className="font-semibold">Browser / Webkit Level</h4>
                  <ul className="mt-1 list-disc space-y-2 pl-5">
                    <li><strong>Safari Specific Settings:</strong> Under <code>Settings &gt; Safari &gt; Location</code> (in main iOS Settings), ensure it is set to <strong>Ask</strong> or <strong>Allow</strong>.</li>
                    <li><strong>Origin Prompt:</strong> The web page prompt must be explicitly accepted (<code>Allow</code>) when requested in the active browser tab.</li>
                  </ul>
                </li>
              </ol>
            </DeviceInstructions>
            <DeviceInstructions title="Desktop Browsers (Windows / macOS)" expanded={expandedIndex === 2} onToggle={() => toggleDevice(2)}>
              <ol className="list-decimal space-y-4 pl-5 marker:font-semibold">
                <li>
                  <h4 className="font-semibold">System Level (Computer Location Settings)</h4>
                  <ul className="mt-1 list-disc space-y-2 pl-5">
                    <li><strong>Windows:</strong> Open <code>Settings &gt; Privacy &amp; security &gt; Location</code> (Windows 11) or <code>Settings &gt; Privacy &gt; Location</code> (Windows 10). Turn location services <strong>ON</strong> and enable app and desktop app location access where available.</li>
                    <li><strong>macOS:</strong> Open <code>System Settings &gt; Privacy &amp; Security &gt; Location Services</code>. Turn <strong>Location Services ON</strong> and enable access for your browser if listed.</li>
                  </ul>
                </li>
                <li>
                  <h4 className="font-semibold">Browser Level (Location Permissions)</h4>
                  <ul className="mt-1 list-disc space-y-2 pl-5">
                    <li><strong>Google Chrome:</strong> Open <code>Settings &gt; Privacy and security &gt; Site settings &gt; Location</code> and allow sites to ask for your location.</li>
                    <li><strong>Microsoft Edge:</strong> In <code>Settings</code>, search for <code>Location</code>, open the location site permissions, and enable <strong>Ask before accessing</strong>.</li>
                    <li><strong>Mozilla Firefox:</strong> In <code>Settings</code>, search for <code>Location</code>. Under <code>Permissions</code>, open <code>Location &gt; Settings</code>, remove this website&apos;s blocked entry, and save changes. Ensure new location requests are not blocked.</li>
                    <li><strong>Safari (Mac):</strong> Open <code>Safari &gt; Settings &gt; Websites &gt; Location</code> and set this website to <strong>Ask</strong> or <strong>Allow</strong>.</li>
                  </ul>
                </li>
                <li>
                  <h4 className="font-semibold">Website Level (Allow and Retry)</h4>
                  <ul className="mt-1 list-disc space-y-2 pl-5">
                    <li><strong>Site Permission:</strong> If you previously blocked location in Chrome or Edge, open the site information icon beside the address bar, open the site permissions, and change <strong>Location</strong> to <strong>Allow</strong> or <strong>Ask</strong>.</li>
                    <li><strong>Origin Prompt:</strong> After updating permissions, reload the Mark Attendance page and select <strong>Allow</strong> when the browser requests location access.</li>
                  </ul>
                </li>
              </ol>
              <p className="mt-3 text-xs per-file-hint">Setting names may vary by browser and operating system version.</p>
            </DeviceInstructions>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
